/**
 * Presentation Library client (#5, migration 0097).
 *
 * Backend router: prefix /presentations.
 *   - GET    /                       list library files (metadata)      — any authenticated user
 *   - POST   /            (multipart) upload a file                     — presentation.manage
 *   - GET    /{id}/download           stream one file (binary)         — any authenticated user
 *   - DELETE /{id}                    soft-delete, 204                  — presentation.manage
 *
 * Browse/download is open to all users; upload/remove requires the `presentation.manage`
 * permission (seeded to admin). The multipart upload + the download use a raw same-origin
 * fetch (the JSON `api` client only speaks JSON), mirroring feeInvoices.ts; reuses downloadBlob.
 */
import { api } from './client';
import { downloadBlob } from './iaaExports';
import type { ISODateTime, UUID } from './types';

export interface PresentationFile {
  id: UUID;
  title: string;
  description: string | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: ISODateTime | null;
}

export interface PresentationUploadInput {
  title: string;
  description?: string | null;
  file: File;
}

/** Pull the file name from a Content-Disposition header (RFC 5987 first). */
function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

export const presentationsApi = {
  /** All alive library files (metadata; never the bytes), newest first. */
  list: (): Promise<PresentationFile[]> =>
    api.get<PresentationFile[]>('/presentations'),

  /** Add one approved file to the library (multipart). 201. Requires presentation.manage. */
  async upload(input: PresentationUploadInput): Promise<PresentationFile> {
    const form = new FormData();
    form.append('title', input.title);
    if (input.description != null && input.description !== '') {
      form.append('description', input.description);
    }
    form.append('file', input.file);
    const res = await fetch('/presentations', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The file could not be uploaded.'));
    return res.json();
  },

  /** Remove a file from the library (soft-delete). 204. Requires presentation.manage. */
  remove: (id: UUID): Promise<void> => api.delete<void>(`/presentations/${id}`),

  /** Download one library file (streams a blob → browser save). */
  async download(id: UUID, fallbackName = 'file'): Promise<void> {
    const res = await fetch(`/presentations/${id}/download`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The file could not be downloaded.'));
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, fallbackName));
  },
};
